import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  Activity,
  CheckCircle,
  MapPin,
  TrendingUp,
  Calendar,
  ArrowUpRight,
  DollarSign,
  Loader,
  BarChart3,
  Map,
  AlertCircle
} from 'lucide-react';
import { emergenciasAPI, departamentosAPI, municipiosAPI } from '../services/apiService';

const Dashboard = () => {
  const [emergencias, setEmergencias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    porAtender: 0,
    enAtencion: 0,
    atendidas: 0,
    inversionTotal: 0,
    inversionProyectada: 0,
    departamentosAfectados: 0,
    municipiosAfectados: 0
  });
  const [topDepartamentos, setTopDepartamentos] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [emergenciasRes, deptRes, munRes] = await Promise.all([
        emergenciasAPI.getAll(),
        departamentosAPI.getAll(),
        municipiosAPI.getAll()
      ]);
      
      // ✅ CORRECCIÓN: Asegurar que siempre sean arrays
      let emergenciasData = emergenciasRes?.data || emergenciasRes || [];
      let deptData = deptRes?.data || deptRes || [];
      let munData = munRes?.data || munRes || [];
      
      // ✅ CORRECCIÓN: Validar que sean arrays
      if (!Array.isArray(emergenciasData)) {
        console.warn('emergenciasData no es un array:', emergenciasData);
        emergenciasData = [];
      }
      if (!Array.isArray(deptData)) {
        console.warn('deptData no es un array:', deptData);
        deptData = [];
      }
      if (!Array.isArray(munData)) {
        console.warn('munData no es un array:', munData);
        munData = [];
      }

      setEmergencias(emergenciasData.slice(0, 8));
      setDepartamentos(deptData);
      setMunicipios(munData);
      
      // Calcular estadísticas reales
      const total = emergenciasData.length;
      const porAtender = emergenciasData.filter(e => e.Estado === 'Por Atender').length;
      const enAtencion = emergenciasData.filter(e => e.Estado === 'En Atención').length;
      const atendidas = emergenciasData.filter(e => e.Estado === 'Atendido').length;
      
      const inversionTotal = emergenciasData.reduce((sum, e) => 
        sum + (parseFloat(e.InversionTotal) || 0), 0
      );
      const inversionProyectada = emergenciasData.reduce((sum, e) => 
        sum + (parseFloat(e.InversionProyectada) || 0), 0
      );

      // Departamentos y municipios únicos
      const deptUnicos = new Set(
        emergenciasData
          .map(e => e.MunicipioFK?.substring(0, 2))
          .filter(Boolean)
      );
      const munUnicos = new Set(
        emergenciasData
          .map(e => e.MunicipioFK)
          .filter(Boolean)
      );
      
      setStats({ 
        total, 
        porAtender, 
        enAtencion, 
        atendidas,
        inversionTotal,
        inversionProyectada,
        departamentosAfectados: deptUnicos.size,
        municipiosAfectados: munUnicos.size
      });

      // Calcular top departamentos
      const deptCount = {};
      emergenciasData.forEach(e => {
        const deptCode = e.MunicipioFK?.substring(0, 2);
        if (deptCode) {
          const dept = deptData.find(d => d.CodigoDepartamento === deptCode);
          const deptName = dept?.NombreDepartamento || `Departamento ${deptCode}`;
          deptCount[deptName] = (deptCount[deptName] || 0) + 1;
        }
      });

      const topDepts = Object.entries(deptCount)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      setTopDepartamentos(topDepts);

    } catch (err) {
      console.error('Error cargando dashboard:', err);
      setError('No se pudo cargar la información del dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="w-16 h-16 text-[#c8a88c] animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-600 font-medium">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-red-700 mb-2">Error al cargar datos</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const statsCards = [
    { 
      title: 'Total Emergencias', 
      value: stats.total, 
      icon: AlertTriangle, 
      color: '#c8a88c',
      bgColor: 'bg-[#c8a88c]',
      percentage: 100,
      description: 'Registradas en el sistema'
    },
    { 
      title: 'Por Atender', 
      value: stats.porAtender, 
      icon: Clock, 
      color: '#F97316',
      bgColor: 'bg-orange-500',
      percentage: stats.total ? (stats.porAtender / stats.total * 100).toFixed(1) : 0,
      description: 'Requieren atención inmediata'
    },
    { 
      title: 'En Atención', 
      value: stats.enAtencion, 
      icon: Activity, 
      color: '#3B82F6',
      bgColor: 'bg-blue-500',
      percentage: stats.total ? (stats.enAtencion / stats.total * 100).toFixed(1) : 0,
      description: 'Siendo atendidas'
    },
    { 
      title: 'Atendidas', 
      value: stats.atendidas, 
      icon: CheckCircle, 
      color: '#10B981',
      bgColor: 'bg-green-500',
      percentage: stats.total ? (stats.atendidas / stats.total * 100).toFixed(1) : 0,
      description: 'Completadas exitosamente'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Resumen </h1>
          <p className="text-gray-500 mt-1">Emergencias FCN</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
          <Calendar className="w-4 h-4 text-gray-600" />
          <span className="text-sm text-gray-600">
            {new Date().toLocaleDateString('es-HN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={DollarSign}
          title="Inversión Total"
          value={`L. ${stats.inversionTotal.toLocaleString()}`}
          subtitle="Monto ejecutado"
          dotColor="bg-[#c8a88c]"
        />
        <MetricCard
          icon={TrendingUp}
          title="Inversión Proyectada"
          value={`L. ${stats.inversionProyectada.toLocaleString()}`}
          subtitle="Monto estimado"
          dotColor="bg-[#F3A108]"
        />
        <MetricCard
          icon={Map}
          title={`${stats.departamentosAfectados} Departamentos`}
          value={stats.departamentosAfectados}
          subtitle="Afectados"
          dotColor="bg-purple-500"
        />
        <MetricCard
          icon={MapPin}
          title={`${stats.municipiosAfectados} Municipios`}
          value={stats.municipiosAfectados}
          subtitle="Afectados"
          dotColor="bg-cyan-500"
        />
      </div>

      {/* Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Emergencias Recientes */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#c8a88c] rounded-lg">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Emergencias Recientes</h3>
                  <p className="text-sm text-gray-600">
                    {emergencias.length > 0 ? `Últimas ${emergencias.length} registradas` : 'No hay emergencias'}
                  </p>
                </div>
              </div>
              <button 
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
              >
                Ver todas
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="p-5 space-y-3 max-h-[600px] overflow-y-auto">
            {emergencias.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No hay emergencias recientes</p>
                <p className="text-sm text-gray-400 mt-2">Las emergencias aparecerán aquí cuando sean registradas</p>
              </div>
            ) : (
              emergencias.map((emergency) => (
                <EmergencyCard key={emergency.Id} emergency={emergency} />
              ))
            )}
          </div>
        </div>

        {/* Panel Lateral */}
        <div className="space-y-4">
          {/* Top Departamentos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#c8a88c] rounded-lg">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Top Departamentos</h3>
                  <p className="text-sm text-gray-600">Más emergencias</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-2">
              {topDepartamentos.length === 0 ? (
                <div className="text-center py-8">
                  <Map className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Sin datos disponibles</p>
                </div>
              ) : (
                topDepartamentos.map((dept, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#c8a88c] rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{dept.name}</div>
                      <div className="text-xs text-gray-500">{dept.count} emergencias</div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="text-lg font-bold text-[#c8a88c]">{dept.count}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, bgColor, percentage, description }) => {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {title}
        </span>
        <div className={`p-2.5 ${bgColor} rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="mb-3">
        <div className="text-3xl font-bold text-gray-900 mb-1">
          {value}
        </div>
        <div className="text-sm text-gray-600">{description}</div>
      </div>

      <div className="flex items-center gap-2">
        <div className="text-xs font-semibold text-gray-500">
          {percentage}%
        </div>
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full ${bgColor} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ icon: Icon, title, value, subtitle, dotColor }) => {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm text-gray-700">{title}</h3>
        <div className={`w-2 h-2 ${dotColor} rounded-full`}></div>
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  );
};

const EmergencyCard = ({ emergency }) => {
  const statusConfig = {
    'Por Atender': {
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      dot: 'bg-orange-500',
      icon: Clock
    },
    'En Atención': {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      dot: 'bg-blue-500',
      icon: Activity
    },
    'Atendido': {
      bg: 'bg-green-50',
      text: 'text-green-700',
      dot: 'bg-green-500',
      icon: CheckCircle
    }
  };

  const config = statusConfig[emergency.Estado] || statusConfig['Por Atender'];
  const StatusIcon = config.icon;

  return (
    <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-[#c8a88c] hover:shadow-sm transition-all">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="relative">
          <div className="w-12 h-12 bg-[#c8a88c] rounded-lg flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div className={`absolute -top-1 -right-1 w-3 h-3 ${config.dot} rounded-full border-2 border-white`}></div>
        </div>
        
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-bold text-gray-900">#{emergency.Id}</div>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-sm font-medium text-gray-700 truncate">{emergency.NombreTipoEmergencia}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{emergency.NombreMunicipio}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>•</span>
            <span className="font-medium">L. {(emergency.InversionTotal || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>
      
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold ${config.bg} ${config.text}`}>
        <StatusIcon className="w-3.5 h-3.5" />
        {emergency.Estado}
      </div>
    </div>
  );
};

export default Dashboard;